import { REQUIRED_SECTIONS, QUESTION_TYPES } from '../api/constants';

/**
 * Validates section content based on question type
 * @param {string} questionType - Type of question being analyzed
 * @param {Object} sections - Parsed sections
 * @returns {Object} Validation results
 */
const validateSections = (questionType, sections) => {
  const validation = {
    isValid: true,
    missingSections: [],
    invalidSections: [],
    warnings: []
  };

  // Check required sections based on question type
  const requiredSections = REQUIRED_SECTIONS[questionType] || REQUIRED_SECTIONS.DEFAULT;
  
  requiredSections.forEach(section => {
    if (!sections[section] || (Array.isArray(sections[section]) && sections[section].length === 0)) {
      validation.missingSections.push(section);
      validation.isValid = false;
    }
  });

  // Validate section content
  Object.entries(sections).forEach(([section, content]) => {
    if (section === '_meta') return;

    if (section === 'executiveSummary') {
      if (!content || content.length < 50) {
        validation.warnings.push(`${section} may be too brief`);
      }
    } else if (Array.isArray(content)) {
      if (content.length < 3) {
        validation.warnings.push(`${section} has fewer than 3 items`);
      }
      if (content.length > 5) {
        validation.warnings.push(`${section} has more than 5 items`);
      }
    }
  });

  return validation;
};

/**
 * Parses markdown content into structured sections
 * @param {string} content - Raw markdown content from OpenAI
 * @param {string} questionType - Type of question being analyzed
 * @returns {Object} Parsed sections with validation info
 */
export const parseMarkdownResponse = (content, questionType) => {
  if (!content) {
    throw new Error('No content provided for parsing');
  }

  // console.log('🔍 Starting to parse markdown response:', { 
  //   contentLength: content.length,
  //   firstFewLines: content.split('\n').slice(0, 5).join('\n'),
  //   timestamp: new Date().toISOString()
  // });

  // Initialize sections with empty values
  const sections = {
    executiveSummary: '',
    analysis: [],       // Dedicated analysis section
    keyThemes: [],
    actionableInsights: [],
    nextSteps: [],
    limitations: '',    // Added limitations section
    surveyDesignFeedback: '', // Added survey design feedback section
    _meta: {
      foundSections: [],
      missingSections: [],
      parsing: {
        startTime: new Date().toISOString(),
        endTime: null,
        duration: null
      },
      validation: null
    }
  };

  const lines = content.split('\n');
  let currentSection = '';
  let sectionContent = [];

  // Track section headers for validation
  const sectionHeaders = {
    'executiveSummary': ['## Executive Summary', '# Executive Summary'],
    'analysis': ['## Analysis', '# Analysis'],  // Dedicated analysis section
    'keyThemes': ['## Key Themes', '# Key Themes', '## Key Insights', '# Key Insights'],
    'actionableInsights': ['## Actionable Insights', '# Actionable Insights'],
    'nextSteps': ['## Next Steps', '# Next Steps', '## Recommendations', '# Recommendations'],
    'limitations': ['## Limitations', '# Limitations'],  // Added limitations section
    'surveyDesignFeedback': ['## Survey Design Feedback', '# Survey Design Feedback', '## Survey Feedback', '# Survey Feedback'],  // Added survey design feedback
    'matrixAnalysis': ['## Matrix Analysis', '# Matrix Analysis'],
    'ratingDistribution': ['## Rating Distribution', '# Rating Distribution'],
    'rankingAnalysis': ['## Ranking Analysis', '# Ranking Analysis'],
    'priorityDistribution': ['## Priority Distribution', '# Priority Distribution']
  };

  // Helper function to check if a line is a header
  const isHeaderLine = (line) => {
    const trimmedLine = line.trim();
    return trimmedLine.startsWith('# ') || 
           trimmedLine.startsWith('## ') || 
           trimmedLine.startsWith('### ');
  };

  // Process each line
  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    // Skip lines before we find first section
    if (!currentSection && !isHeaderLine(trimmedLine)) {
      return;
    }
    
    // Check for section headers
    let foundSection = false;
    for (const [section, headers] of Object.entries(sectionHeaders)) {
      if (headers.some(header => 
        trimmedLine === header || 
        trimmedLine.startsWith(header + ':') || 
        trimmedLine.toLowerCase() === header.toLowerCase()
      )) {
        // If we were processing a section, save its content
        if (currentSection && sectionContent.length > 0) {
          processSection(sections, currentSection, sectionContent);
        }
        currentSection = section;
        sections._meta.foundSections.push(section);
        sectionContent = [];
        foundSection = true;
        // console.log(`🔍 Found section: ${section} with header: ${trimmedLine}`);
        break;
      }
    }

    if (foundSection) {
      return;
    }

    // If we hit another header that's not in our list, it could be the end of a section
    if (currentSection && isHeaderLine(trimmedLine) && !foundSection) {
      // Save the current section
      if (sectionContent.length > 0) {
        processSection(sections, currentSection, sectionContent);
        // console.log(`🔍 Processed section: ${currentSection} with ${sectionContent.length} lines`);
      }
      // Reset for unknown section
      currentSection = '';
      sectionContent = [];
      return;
    }

    // Skip empty lines at the start of sections
    if (!trimmedLine && sectionContent.length === 0) {
      return;
    }

    // Add content to current section
    if (currentSection) {
      sectionContent.push(trimmedLine);
    }
  });

  // Process the last section
  if (currentSection && sectionContent.length > 0) {
    processSection(sections, currentSection, sectionContent);
    // console.log(`🔍 Processed final section: ${currentSection} with ${sectionContent.length} lines`);
  }

  // Complete metadata
  sections._meta.parsing.endTime = new Date().toISOString();
  sections._meta.parsing.duration = new Date(sections._meta.parsing.endTime) - new Date(sections._meta.parsing.startTime);
  
  // Validate sections
  sections._meta.validation = validateSections(questionType, sections);
  sections._meta.missingSections = sections._meta.validation.missingSections;

  // console.log('✅ Markdown parsing complete:', {
  //   foundSections: sections._meta.foundSections,
  //   missingSections: sections._meta.missingSections,
  //   executiveSummaryLength: sections.executiveSummary?.length || 0,
  //   keyThemesCount: sections.keyThemes?.length || 0,
  //   actionableInsightsCount: sections.actionableInsights?.length || 0,
  //   nextStepsCount: sections.nextSteps?.length || 0,
  //   duration: sections._meta.parsing.duration,
  //   timestamp: new Date().toISOString()
  // });
  
  return sections;
};

/**
 * Process collected content for a section
 * @param {Object} sections - Sections object to update
 * @param {string} sectionName - Name of the section
 * @param {string[]} content - Lines of content for the section
 */
/**
 * Process collected content for a section
 * @param {Object} sections - Sections object to update
 * @param {string} sectionName - Name of the section
 * @param {string[]} content - Lines of content for the section
 */
const processSection = (sections, sectionName, content) => {
  // Skip any lines that start with a header
  const filteredContent = content.filter(line => {
    const trimmed = line.trim();
    return !trimmed.startsWith('#') && trimmed.length > 0;
  });

  if (sectionName === 'executiveSummary' || sectionName === 'limitations' || sectionName === 'surveyDesignFeedback') {
    // Join all non-empty lines for text-based sections
    sections[sectionName] = filteredContent.join('\n');
    return;
  }
  
  // For other sections, process bullet points and paragraphs
  let currentPoint = '';
  const points = [];

  // These are all the bullet styles we want to detect
  const bulletStyles = ['- ', '• ', '* ', '→ ', '▪ ', '○ ', '💡 ', '🔍 ', '📊 '];
  
  // Process each line
  filteredContent.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    // Skip empty lines
    if (!trimmedLine) {
      // If we have content in currentPoint, add it and reset
      if (currentPoint) {
        points.push(currentPoint);
        currentPoint = '';
      }
      return;
    }
    
    // Check if this line starts with a bullet point
    let isBulletPoint = false;
    for (const style of bulletStyles) {
      if (trimmedLine.startsWith(style)) {
        // If we have content in currentPoint, add it and reset
        if (currentPoint) {
          points.push(currentPoint);
        }
        // Start a new point
        currentPoint = trimmedLine.substring(style.length).trim();
        isBulletPoint = true;
        break;
      }
    }
    
    // If it starts with a number and period (e.g., "1.") it's also a bullet
    if (!isBulletPoint && /^\d+\.\s/.test(trimmedLine)) {
      // If we have content in currentPoint, add it and reset
      if (currentPoint) {
        points.push(currentPoint);
      }
      // Start a new point, removing the number prefix
      currentPoint = trimmedLine.replace(/^\d+\.\s/, '');
      isBulletPoint = true;
    }
    
    // If it's not a bullet point, and we have text in currentPoint, append it
    if (!isBulletPoint) {
      if (currentPoint) {
        // Append to current point with a space
        currentPoint += ' ' + trimmedLine;
      } else {
        // Start a new point if it's a new paragraph
        currentPoint = trimmedLine;
      }
    }
  });
  
  // Add the last point if we have content
  if (currentPoint) {
    points.push(currentPoint);
  }
  
  // Only keep non-empty points
  sections[sectionName] = points.filter(point => point && point.trim().length > 0);
}; 